import devtools from "devtools-detect";

class Browser {
    constructor() {
        this.handlers = {};
        this.callbacks = {
            on_violation_detected: null,
            show_notification: null,
        };
        this.devtools_open = false;
    }

    /**
     * Detects tab/visibility changes
     */
    detectVisibilityChange() {
        const handler = () => {
            if (document.visibilityState === "hidden") {
                if (this.callbacks.show_notification) {
                    this.callbacks.show_notification(
                        "Tab Switched",
                        "You have switched tabs. This has been recorded.",
                        "warning",
                    );
                }
                if (this.callbacks.on_violation_detected) {
                    setTimeout(() => {
                        this.callbacks.on_violation_detected("tab_switch");
                    }, 700);
                }
            }
        };

        this.handlers.visibility_change = handler;
        document.addEventListener("visibilitychange", handler);
    }

    /**
     * Detects and blocks paste attempts
     */
    detectPaste() {
        const handler = (event) => {
            event.preventDefault();
            if (this.callbacks.show_notification) {
                this.callbacks.show_notification(
                    "Action Blocked",
                    "Pasting content is disabled during the exam.",
                    "warning",
                );
            }
            if (this.callbacks.on_violation_detected) {
                this.callbacks.on_violation_detected("paste_attempt");
            }

            // Note: Modern browsers largely ignore keyCode, but kept for compatibility
            const blocked_keys = [
                event.ctrlKey && event.keyCode === 67, // Ctrl+C
                event.ctrlKey && event.keyCode === 86, // Ctrl+V
            ];

            if (blocked_keys.some((blocked) => blocked)) {
                if (event.ctrlKey && event.keyCode === 86) {
                    if (this.callbacks.on_violation_detected) {
                        this.callbacks.on_violation_detected("paste_attempt");
                    }
                }
            }

            return false;
        };

        this.handlers.paste = handler;
        document.addEventListener("paste", handler, false);
    }

    /**
     * Detects and blocks context menu (right-click)
     */
    detectContextMenu() {
        const handler = (event) => {
            event.preventDefault();
            if (this.callbacks.show_notification) {
                this.callbacks.show_notification(
                    "Action Blocked",
                    "Right-click menu is disabled during the exam.",
                    "warning",
                );
            }
            if (this.callbacks.on_violation_detected) {
                this.callbacks.on_violation_detected("context_menu");
            }
            return false;
        };

        this.handlers.context_menu = handler;
        document.addEventListener("contextmenu", handler, false);
    }

    /**
     * Detects and blocks DevTools
     */
    async detectDevTools() {
        let is_mac = false;

        if (
            navigator.userAgentData &&
            navigator.userAgentData.getHighEntropyValues
        ) {
            const platform_info =
                await navigator.userAgentData.getHighEntropyValues([
                    "platform",
                ]);
            is_mac = platform_info.platform === "macOS";
        } else {
            is_mac = /Mac|iPhone|iPod|iPad/.test(
                navigator.userAgent || navigator.platform,
            );
        }

        const keyboard_handler = (event) => {
            const cmd_or_ctrl = is_mac ? event.metaKey : event.ctrlKey;
            const cmd_or_opt = is_mac ? event.metaKey && event.altKey : false;

            const blocked_keys = [
                event.keyCode === 123, // F12
                cmd_or_ctrl && event.shiftKey && event.keyCode === 73, // Ctrl+Shift+I
                cmd_or_ctrl && event.shiftKey && event.keyCode === 74, // Ctrl+Shift+J
                cmd_or_ctrl && event.shiftKey && event.keyCode === 67, // Ctrl+Shift+C
                cmd_or_ctrl && event.keyCode === 85, // Ctrl+U (View Source)
                cmd_or_opt && event.keyCode === 73, // Cmd+Opt+I (Mac)
            ];

            if (blocked_keys.some((blocked) => blocked)) {
                event.preventDefault();
                if (this.callbacks.show_notification) {
                    this.callbacks.show_notification(
                        "Action Blocked",
                        "This keyboard shortcut is disabled during the exam.",
                        "warning",
                    );
                }
                return false;
            }
        };

        const dev_tools_change_handler = (event) => {
            const is_open = event.detail.isOpen;
            this.devtools_open = is_open;

            if (is_open) {
                if (this.callbacks.show_notification) {
                    this.callbacks.show_notification(
                        "Developer Tools Detected",
                        "Developer tools have been opened. This activity is being monitored.",
                        "warning",
                    );
                }
                if (this.callbacks.on_violation_detected) {
                    this.callbacks.on_violation_detected("devtools_opened");
                }
            }
        };

        this.handlers.keyboard_shortcuts = keyboard_handler;
        this.handlers.dev_tools_change = dev_tools_change_handler;

        window.addEventListener("keydown", keyboard_handler);
        window.addEventListener("devtoolschange", dev_tools_change_handler);
    }

    /**
     * Detects and blocks drag over events
     */
    detectDragOver() {
        const handler = (event) => {
            event.preventDefault();
        };

        this.handlers.drag_over = handler;
        document.addEventListener("dragover", handler, false);
    }

    /**
     * Detects and blocks drag and drop events
     */
    detectDragDrop() {
        const handler = (event) => {
            event.preventDefault();
            if (this.callbacks.show_notification) {
                this.callbacks.show_notification(
                    "Action Blocked",
                    "Drag and drop operations are disabled during the exam.",
                    "warning",
                );
            }
            if (this.callbacks.on_violation_detected) {
                this.callbacks.on_violation_detected("drag_drop_detected");
            }
            return false;
        };

        this.handlers.drag_drop = handler;
        document.addEventListener("drop", handler, false);
    }

    /**
     * Gets the current DevTools status
     */
    getDevToolsStatus() {
        return {
            is_open: this.devtools_open,
            last_checked: new Date().toISOString(),
        };
    }

    stop() {
        if (this.handlers.visibility_change) {
            document.removeEventListener(
                "visibilitychange",
                this.handlers.visibility_change,
            );
        }
        if (this.handlers.paste) {
            document.removeEventListener("paste", this.handlers.paste, false);
        }
        if (this.handlers.context_menu) {
            document.removeEventListener(
                "contextmenu",
                this.handlers.context_menu,
                false,
            );
        }
        if (this.handlers.keyboard_shortcuts) {
            window.removeEventListener(
                "keydown",
                this.handlers.keyboard_shortcuts,
                false,
            );
        }
        if (this.handlers.dev_tools_change) {
            window.removeEventListener(
                "devtoolschange",
                this.handlers.dev_tools_change,
            );
        }
        if (this.handlers.drag_over) {
            document.removeEventListener(
                "dragover",
                this.handlers.drag_over,
                false,
            );
        }
        if (this.handlers.drag_drop) {
            document.removeEventListener(
                "drop",
                this.handlers.drag_drop,
                false,
            );
        }

        this.handlers = {};
    }

    init(options = {}) {
        this.callbacks.on_violation_detected =
            options.on_violation_detected ||
            options.onViolationDetected ||
            null;
        this.callbacks.show_notification =
            options.show_notification || options.showNotification || null;

        this.detectVisibilityChange();
        this.detectPaste();
        this.detectContextMenu();
        this.detectDevTools();
        this.detectDragOver();
        this.detectDragDrop();
    }
}

export default new Browser();
